import React, { useState } from "react";
import {
  Card,
  Stack,
  Group,
  Text,
  TextInput,
  Select,
  NumberInput,
  Button,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { getApiEndpoint } from "../../config/config";
import { handleApiError, handleGenericError } from "../../utils/errorHandler";

const BONUS_CATEGORY = {
  ITEM: "ITEM",
  MONEY: "MONEY",
  REOCCURRING: "REOCCURRING",
};
const ITEM_EARNED_BY = {
  REVENUE: "REVENUE",
  ACCOUNTS: "ACCOUNTS",
  RECRUITS: "RECRUITS",
};

const BONUS_CATEGORY_OPTIONS = [
  { value: "ITEM", label: "Item" },
  { value: "MONEY", label: "Money" },
  { value: "REOCCURRING", label: "Reoccurring" },
];

// Labels for the category values that come back on ps_company_bonus_types. The
// company types use RECURRING; the legacy form value is REOCCURRING — treat both
// as the same recurring category.
const BONUS_CATEGORY_LABEL_BY_VALUE = {
  ITEM: "Item",
  MONEY: "Money",
  RECURRING: "Recurring",
  REOCCURRING: "Reoccurring",
};

const isRecurringCategory = value =>
  value === "RECURRING" || value === "REOCCURRING";

// Pull the category value off a ps_company_bonus_types entry, tolerating the few
// keys the overview may use.
const getCompanyBonusType = entry =>
  entry?.type ?? entry?.bonus_type_category ?? entry?.bonus_type ?? null;

const isCompanyTypeActive = entry =>
  entry?.is_active !== false && entry?.active !== false;
const ITEM_EARNED_BY_OPTIONS = [
  { value: "REVENUE", label: "Revenue" },
  { value: "ACCOUNTS", label: "Accounts" },
  { value: "RECRUITS", label: "Recruits" },
];
const EARNED_BY_TYPE_REVENUE_ACCOUNTS = [
  { value: "SERVICED", label: "Serviced" },
  { value: "QUALIFIED", label: "Qualified" },
];
const EARNED_BY_TYPE_RECRUITS = [
  { value: "GET_TO_MARKET", label: "Get to Market" },
  { value: "FINISH_SUMMER", label: "Finish Summer" },
];
const SCHEDULE_TYPE_OPTIONS = [
  { value: "WEEKLY", label: "Weekly" },
  { value: "BIWEEKLY", label: "Biweekly" },
  { value: "MONTHLY", label: "Monthly" },
];
const DAY_OF_WEEK_OPTIONS = [
  { value: "1", label: "Sunday" },
  { value: "2", label: "Monday" },
  { value: "3", label: "Tuesday" },
  { value: "4", label: "Wednesday" },
  { value: "5", label: "Thursday" },
  { value: "6", label: "Friday" },
  { value: "7", label: "Saturday" },
];
const DAY_OF_MONTH_OPTIONS = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));

const toISODateString = date => {
  if (!date) return null;
  if (typeof date === "string") {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  }
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Form for adding a bonus. In `inline` mode it builds the bonus object in the
// deal payload shape and hands it back through onCreated without an API call;
// otherwise it saves a new default bonus via the API and returns the created
// record. When `companyBonusTypes` is provided, the category options come from
// the company's active bonus types.
function AddBonusForm({
  sellingYearId,
  onCreated,
  onCancel,
  inline = false,
  companyBonusTypes = null,
}) {
  const { getVerifiedJWT } = useAuth();
  const { showError } = useToast();

  const categoryOptions = Array.isArray(companyBonusTypes)
    ? companyBonusTypes
        .filter(isCompanyTypeActive)
        .map(entry => {
          const value = getCompanyBonusType(entry);
          return value
            ? { value, label: BONUS_CATEGORY_LABEL_BY_VALUE[value] ?? value }
            : null;
        })
        .filter(Boolean)
    : BONUS_CATEGORY_OPTIONS;

  const [name, setName] = useState("");
  const [bonusCategory, setBonusCategory] = useState(null);
  const [itemEarnedBy, setItemEarnedBy] = useState(null);
  const [earnedByType, setEarnedByType] = useState(null);
  const [bonusAmount, setBonusAmount] = useState("");
  const [revenueAmount, setRevenueAmount] = useState("");
  const [accountsAmount, setAccountsAmount] = useState("");
  const [recruitsAmount, setRecruitsAmount] = useState("");
  const [itemName, setItemName] = useState("");
  const [itemMonetaryValue, setItemMonetaryValue] = useState("");
  const [reoccuringInitialAmount, setReoccuringInitialAmount] = useState("");
  const [reoccuringRepeatedAmount, setReoccuringRepeatedAmount] = useState("");
  const [reoccuringStartDate, setReoccuringStartDate] = useState(null);
  const [reoccuringEndDate, setReoccuringEndDate] = useState(null);
  const [reoccuringScheduleType, setReoccuringScheduleType] = useState(null);
  const [reoccuringDayOfWeek, setReoccuringDayOfWeek] = useState(null);
  const [reoccuringDayOfMonth, setReoccuringDayOfMonth] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const earnedByTypeOptions =
    itemEarnedBy === ITEM_EARNED_BY.RECRUITS
      ? EARNED_BY_TYPE_RECRUITS
      : itemEarnedBy
        ? EARNED_BY_TYPE_REVENUE_ACCOUNTS
        : [];

  const validate = () => {
    if (!name.trim()) return (showError("Please enter a name"), false);
    if (!bonusCategory)
      return (showError("Please select a bonus category"), false);
    if (!itemEarnedBy)
      return (showError("Please select what the item is earned by"), false);
    if (!earnedByType)
      return (showError("Please select an earned by type"), false);
    if (itemEarnedBy === ITEM_EARNED_BY.REVENUE && revenueAmount === "")
      return (showError("Please enter a revenue amount"), false);
    if (itemEarnedBy === ITEM_EARNED_BY.ACCOUNTS && accountsAmount === "")
      return (showError("Please enter an accounts amount"), false);
    if (itemEarnedBy === ITEM_EARNED_BY.RECRUITS && recruitsAmount === "")
      return (showError("Please enter a recruits amount"), false);
    if (bonusCategory === BONUS_CATEGORY.MONEY && bonusAmount === "")
      return (showError("Please enter a bonus amount"), false);
    if (bonusCategory === BONUS_CATEGORY.ITEM) {
      if (!itemName.trim())
        return (showError("Please enter an item name"), false);
      if (itemMonetaryValue === "")
        return (showError("Please enter an item monetary value"), false);
    }
    if (isRecurringCategory(bonusCategory)) {
      if (reoccuringInitialAmount === "")
        return (showError("Please enter a reoccurring initial amount"), false);
      if (reoccuringRepeatedAmount === "")
        return (showError("Please enter a reoccurring repeated amount"), false);
      if (!reoccuringStartDate)
        return (showError("Please select a reoccurring start date"), false);
      if (!reoccuringEndDate)
        return (showError("Please select a reoccurring end date"), false);
      if (!reoccuringScheduleType)
        return (showError("Please select a reoccurring schedule type"), false);
      if (reoccuringScheduleType === "MONTHLY" && !reoccuringDayOfMonth)
        return (showError("Please select a day of the month"), false);
      if (reoccuringScheduleType !== "MONTHLY" && !reoccuringDayOfWeek)
        return (showError("Please select a day of the week"), false);
    }
    return true;
  };

  const buildPayload = () => {
    const payload = {
      name: name.trim(),
      bonus_category: bonusCategory,
      item_earned_by: itemEarnedBy,
      earned_by_type: earnedByType,
      selling_year_id: sellingYearId,
    };
    if (bonusCategory === BONUS_CATEGORY.MONEY) {
      payload.bonus_amount = Number(bonusAmount);
    }
    if (itemEarnedBy === ITEM_EARNED_BY.REVENUE) {
      payload.revenue_amount = Number(revenueAmount);
    } else if (itemEarnedBy === ITEM_EARNED_BY.ACCOUNTS) {
      payload.accounts_amount = Number(accountsAmount);
    } else if (itemEarnedBy === ITEM_EARNED_BY.RECRUITS) {
      payload.recruits_amount = Number(recruitsAmount);
    }
    if (bonusCategory === BONUS_CATEGORY.ITEM) {
      payload.item_name = itemName.trim();
      payload.item_monetary_value = String(itemMonetaryValue);
    } else if (isRecurringCategory(bonusCategory)) {
      payload.reoccuring_initial_amount = Number(reoccuringInitialAmount);
      payload.reoccuring_repeated_amount = Number(reoccuringRepeatedAmount);
      payload.reoccuring_start_date = toISODateString(reoccuringStartDate);
      payload.reoccuring_end_date = toISODateString(reoccuringEndDate);
      payload.reoccuring_schedule_type = reoccuringScheduleType;
      if (reoccuringScheduleType === "MONTHLY") {
        payload.reoccuring_day_of_month = Number(reoccuringDayOfMonth);
      } else if (reoccuringScheduleType) {
        payload.reoccuring_day_of_week = Number(reoccuringDayOfWeek);
      }
    }
    return payload;
  };

  // The bonus in the deal payload shape (matches what buildBonusPayload /
  // BonusDisplay expect), built straight from the form for the inline flow.
  const buildInlineBonus = () => {
    const bonus = {
      name: name.trim(),
      bonus_type_category: bonusCategory,
      bonus_type_earned_by_item: itemEarnedBy,
      bonus_type_earned_by_type: earnedByType,
      item_name: null,
      item_monetary_value: null,
      bonus_amount: null,
      bonus_revenue_amount: null,
      bonus_accounts_amount: null,
      bonus_recruits_amount: null,
      recurring_initial_amount: null,
      recurring_repeated_amount: null,
      recurring_start_date: null,
      recurring_end_date: null,
      recurring_schedule_type: null,
      recurring_day_of_week: null,
      recurring_day_of_month: null,
    };
    if (bonusCategory === BONUS_CATEGORY.MONEY) {
      bonus.bonus_amount = Number(bonusAmount);
    }
    if (itemEarnedBy === ITEM_EARNED_BY.REVENUE) {
      bonus.bonus_revenue_amount = Number(revenueAmount);
    } else if (itemEarnedBy === ITEM_EARNED_BY.ACCOUNTS) {
      bonus.bonus_accounts_amount = Number(accountsAmount);
    } else if (itemEarnedBy === ITEM_EARNED_BY.RECRUITS) {
      bonus.bonus_recruits_amount = Number(recruitsAmount);
    }
    if (bonusCategory === BONUS_CATEGORY.ITEM) {
      bonus.item_name = itemName.trim();
      bonus.item_monetary_value = String(itemMonetaryValue);
    } else if (isRecurringCategory(bonusCategory)) {
      bonus.recurring_initial_amount = Number(reoccuringInitialAmount);
      bonus.recurring_repeated_amount = Number(reoccuringRepeatedAmount);
      bonus.recurring_start_date = toISODateString(reoccuringStartDate);
      bonus.recurring_end_date = toISODateString(reoccuringEndDate);
      bonus.recurring_schedule_type = reoccuringScheduleType;
      if (reoccuringScheduleType === "MONTHLY") {
        bonus.recurring_day_of_month = Number(reoccuringDayOfMonth);
      } else if (reoccuringScheduleType) {
        bonus.recurring_day_of_week = Number(reoccuringDayOfWeek);
      }
    }
    return bonus;
  };

  const handleSave = async () => {
    if (!validate()) return;

    // Inline flow: no API call, just hand the bonus back to the wizard.
    if (inline) {
      if (onCreated) onCreated(buildInlineBonus());
      return;
    }

    try {
      setSubmitting(true);
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");

      const response = await fetch(getApiEndpoint("/v1/ps/default-bonus"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(buildPayload()),
      });

      if (response.ok) {
        const result = await response.json();
        const created = result?.data || result || {};
        if (onCreated) onCreated(created);
      } else {
        const errorData = await response.json().catch(() => ({}));
        handleApiError(
          new Error(errorData.message || `HTTP ${response.status}`),
          errorData.message || "Failed to create bonus"
        );
      }
    } catch (err) {
      handleGenericError(err, "Failed to create bonus");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card padding="md" radius="md" withBorder>
      <Stack gap="md">
        <Text size="sm" fw={600}>
          New Bonus
        </Text>

        <TextInput
          label="Name"
          placeholder="e.g., Revenue Milestone Bonus"
          value={name}
          onChange={e => setName(e.target.value)}
          required
        />

        <Select
          label="Bonus Category"
          placeholder="Select bonus category"
          data={categoryOptions}
          value={bonusCategory}
          onChange={setBonusCategory}
          required
        />

        <Select
          label="Item Earned By"
          placeholder="Select what the item is earned by"
          data={ITEM_EARNED_BY_OPTIONS}
          value={itemEarnedBy}
          onChange={value => {
            setItemEarnedBy(value);
            setEarnedByType(null);
          }}
          required
        />

        <Select
          label="Earned By Type"
          placeholder="Select earned by type"
          data={earnedByTypeOptions}
          value={earnedByType}
          onChange={setEarnedByType}
          disabled={!itemEarnedBy}
          required
        />

        {itemEarnedBy === ITEM_EARNED_BY.REVENUE && (
          <NumberInput
            label="Revenue Amount"
            placeholder="Enter revenue amount"
            value={revenueAmount}
            onChange={setRevenueAmount}
            min={0}
            prefix="$"
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
          />
        )}
        {itemEarnedBy === ITEM_EARNED_BY.ACCOUNTS && (
          <NumberInput
            label="Accounts Amount"
            placeholder="Enter accounts amount"
            value={accountsAmount}
            onChange={setAccountsAmount}
            min={0}
          />
        )}
        {itemEarnedBy === ITEM_EARNED_BY.RECRUITS && (
          <NumberInput
            label="Recruits Amount"
            placeholder="Enter recruits amount"
            value={recruitsAmount}
            onChange={setRecruitsAmount}
            min={0}
          />
        )}

        {bonusCategory === BONUS_CATEGORY.MONEY && (
          <NumberInput
            label="Bonus Amount"
            placeholder="Enter bonus amount"
            value={bonusAmount}
            onChange={setBonusAmount}
            min={0}
            prefix="$"
            thousandSeparator=","
            decimalScale={2}
            fixedDecimalScale
          />
        )}

        {bonusCategory === BONUS_CATEGORY.ITEM && (
          <>
            <TextInput
              label="Item Name"
              placeholder="Enter item name"
              value={itemName}
              onChange={e => setItemName(e.target.value)}
            />
            <NumberInput
              label="Item Monetary Value"
              placeholder="Enter item monetary value"
              value={itemMonetaryValue}
              onChange={setItemMonetaryValue}
              min={0}
              prefix="$"
              thousandSeparator=","
              decimalScale={2}
              fixedDecimalScale
            />
          </>
        )}

        {isRecurringCategory(bonusCategory) && (
          <>
            <NumberInput
              label="Reoccurring Initial Amount"
              placeholder="Enter initial amount"
              value={reoccuringInitialAmount}
              onChange={setReoccuringInitialAmount}
              min={0}
              prefix="$"
              thousandSeparator=","
              decimalScale={2}
              fixedDecimalScale
            />
            <NumberInput
              label="Reoccurring Repeated Amount"
              placeholder="Enter repeated amount"
              value={reoccuringRepeatedAmount}
              onChange={setReoccuringRepeatedAmount}
              min={0}
              prefix="$"
              thousandSeparator=","
              decimalScale={2}
              fixedDecimalScale
            />
            <DateInput
              label="Reoccurring Start Date"
              placeholder="Select start date"
              value={reoccuringStartDate}
              onChange={setReoccuringStartDate}
            />
            <DateInput
              label="Reoccurring End Date"
              placeholder="Select end date"
              value={reoccuringEndDate}
              onChange={setReoccuringEndDate}
            />
            <Select
              label="Reoccurring Schedule Type"
              placeholder="Select schedule type"
              data={SCHEDULE_TYPE_OPTIONS}
              value={reoccuringScheduleType}
              onChange={setReoccuringScheduleType}
            />
            {(reoccuringScheduleType === "WEEKLY" ||
              reoccuringScheduleType === "BIWEEKLY") && (
              <Select
                label="Reoccurring Day of Week"
                placeholder="Select day of week"
                data={DAY_OF_WEEK_OPTIONS}
                value={reoccuringDayOfWeek}
                onChange={setReoccuringDayOfWeek}
              />
            )}
            {reoccuringScheduleType === "MONTHLY" && (
              <Select
                label="Reoccurring Day of Month"
                placeholder="Select day of month"
                data={DAY_OF_MONTH_OPTIONS}
                value={reoccuringDayOfMonth}
                onChange={setReoccuringDayOfMonth}
                searchable
              />
            )}
          </>
        )}

        <Group justify="flex-end">
          {onCancel && (
            <Button variant="subtle" onClick={onCancel} disabled={submitting}>
              Cancel
            </Button>
          )}
          <Button onClick={handleSave} loading={submitting}>
            {inline ? "Add Bonus" : "Save Bonus"}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

export default AddBonusForm;
